/* eslint-disable @typescript-eslint/no-empty-function */
import React, {
  useReducer,
  createContext,
  ReactElement,
  useContext,
  useState,
} from 'react';
import { INotifOptions } from '../../utils/useNotif';

interface ModalManagerContextProps {
  data: any;
  firstStepShown: boolean;
  flowIndex: number;
  flowKey: string;
  goToFirstStep: () => void;
  isOpen: boolean;
  jumpSteps: (stepsToJump: number) => void;
  loadPreviousFlowStep: () => void;
  loadNextFlowStep: () => void;
  onClose: (message?: string | ReactElement, options?: INotifOptions) => void;
  setData: (data: any) => void;
  startFlow: (key?: string) => void;
}

const ModalManagerContext = createContext<ModalManagerContextProps>({
  data: undefined,
  firstStepShown: true,
  flowIndex: 0,
  flowKey: '',
  goToFirstStep: () => {},
  isOpen: false,
  jumpSteps: (stepsToJump: number) => {},
  loadPreviousFlowStep: () => {},
  loadNextFlowStep: () => {},
  onClose: (message?: string | ReactElement, options?: INotifOptions) => {},
  setData: (data: any) => {},
  startFlow: (key?: string) => {},
});
ModalManagerContext.displayName = 'ModalManagerContext';

interface ModalManagerProviderProps {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
}

const initialState = { history: [], flowIndex: 0 };

function reducer(state, action) {
  switch (action.type) {
    case 'pushHistory':
      return {
        ...state,
        history: [...state.history, action.payload],
        flowIndex: action.payload,
      };
    case 'popHistory': {
      const newHistory = state.history.slice(0, -1);
      return {
        ...state,
        history: newHistory,
        flowIndex: newHistory[newHistory.length - 1],
      };
    }
    case 'setFlowIndex':
      return { ...state, flowIndex: action.payload };
    default:
      return state;
  }
}

export const ModalManagerProvider: React.FC<ModalManagerProviderProps> = ({
  children,
  isOpen,
  onClose,
}) => {
  const [data, setData] = useState();
  const [firstStepShown, setFirstStepShown] = useState(true);
  const [flowKey, setFlowKey] = useState('');
  const [state, dispatch] = useReducer(reducer, initialState);
  const [lastFlowIndex, setLastFlowIndex] = useState<number | undefined>();

  const goToFirstStep = () => {
    setFirstStepShown(true);
    setFlowKey('');
    dispatch({ type: 'pushHistory', payload: 0 });
    if (lastFlowIndex === undefined) {
      onClose();
    } else {
      setLastFlowIndex(undefined);
    }
  };

  const startFlow = (key?: string) => {
    setFirstStepShown(false);
    setFlowKey(key || '');
    setLastFlowIndex(0);
    dispatch({ type: 'pushHistory', payload: 0 });
  };

  const loadNextFlowStep = () => {
    setLastFlowIndex(state.flowIndex);
    dispatch({ type: 'pushHistory', payload: state.flowIndex + 1 });
  };

  const loadPreviousFlowStep = () => {
    if (state.flowIndex === 0 || lastFlowIndex === undefined) {
      goToFirstStep();
      dispatch({ type: 'popHistory' });
    } else {
      dispatch({ type: 'popHistory' });
    }
  };

  const jumpSteps = (stepsToJump: number) => {
    const currentFlowIndex =
      stepsToJump < 0
        ? state.flowIndex + stepsToJump - 1
        : state.flowIndex + stepsToJump + 1;
    setLastFlowIndex(state.flowIndex);
    dispatch({ type: 'pushHistory', payload: currentFlowIndex });
  };

  return (
    <ModalManagerContext.Provider
      value={{
        data,
        firstStepShown,
        flowIndex: state.flowIndex,
        flowKey,
        goToFirstStep,
        isOpen,
        jumpSteps,
        loadPreviousFlowStep,
        loadNextFlowStep,
        onClose,
        setData,
        startFlow,
      }}
    >
      {children}
    </ModalManagerContext.Provider>
  );
};

export const useModalManagerContext = (): ModalManagerContextProps => {
  const context = useContext(ModalManagerContext);

  if (context === undefined) {
    throw new Error(
      'useModalManagerContext must be used within a ModalManagerProvider',
    );
  }

  return context;
};

export default ModalManagerContext;
